import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import QRGenerator from '../components/QRGenerator';
import { invitationsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const AdminInvitations = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [generating, setGenerating] = useState(false);
  const [customCodeForm, setCustomCodeForm] = useState({
    type: 'CUSTOM',
    quantity: 1,
    credits: 1000,
    duration: 7,
    description: ''
  });
  const { user } = useAuth();
  const navigate = useNavigate();

  // Check if user is admin
  useEffect(() => {
    const adminEmails = ['jorge.daza@gmail.com', 'jorge.daza039@gmail.com', 'verayerka239@gmail.com'];
    if (user && !adminEmails.includes(user.email) && !user.is_admin && user.plan !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    loadCodes();
  }, []);

  const loadCodes = async () => {
    try {
      setLoading(true);
      const response = await invitationsAPI.listCodes();
      setCodes(response.data);
    } catch (err) {
      setError('Error al cargar los códigos de invitación');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const generateCodes = async (codeType, quantity) => {
    try {
      setGenerating(true);
      setError('');
      setSuccess('');
      
      const response = await invitationsAPI.generateCodes({
        code_type: codeType,
        quantity: quantity
      });
      
      setSuccess(`${quantity} código(s) de tipo ${codeType} generado(s) exitosamente`);
      await loadCodes();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al generar códigos');
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setSuccess(`Código ${code} copiado al portapapeles`);
    setTimeout(() => setSuccess(''), 2000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('es-CL');
  };

  const unusedInfluencerCodes = codes.filter(c => c.code_type === 'INFLUENCER' && !c.used);
  const unusedTrialCodes = codes.filter(c => c.code_type === 'TRIAL15' && !c.used);
  const usedCodes = codes.filter(c => c.used);

  return (
    <div className="font-display bg-background-light dark:bg-background-dark min-h-screen">
      <div className="px-4 sm:px-10 md:px-20 lg:px-40">
        <Header />
        
        <div className="py-12">
          <div className="mb-8">
            <h1 className="text-4xl font-black text-gray-900 dark:text-white mb-2">
              🎟️ Administración de Códigos de Invitación
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Genera y gestiona códigos promocionales para usuarios
            </p>
          </div>

          {/* Alerts */}
          {error && (
            <div className="mb-4 p-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-400 rounded-lg">
              {error}
            </div>
          )}
          
          {success && (
            <div className="mb-4 p-4 bg-green-100 dark:bg-green-900/30 border border-green-400 dark:border-green-700 text-green-700 dark:text-green-400 rounded-lg">
              {success}
            </div>
          )}

          {/* Generate Codes Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Influencer Codes */}
            <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-6 text-white">
              <h3 className="text-2xl font-bold mb-2">🌟 Códigos VIP Influencers</h3>
              <p className="text-sm mb-4 opacity-90">
                1 mes gratis del Plan Estándar (15,000 créditos)
              </p>
              <button
                onClick={() => generateCodes('INFLUENCER', 5)}
                disabled={generating}
                className="w-full bg-white text-purple-600 font-bold py-3 px-6 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                {generating ? 'Generando...' : 'Generar 5 Códigos VIP'}
              </button>
              <p className="text-xs mt-3 opacity-75">
                Códigos disponibles sin usar: {unusedInfluencerCodes.length}
              </p>
            </div>

            {/* Trial Codes */}
            <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl p-6 text-white">
              <h3 className="text-2xl font-bold mb-2">⏳ Códigos Prueba Temporal</h3>
              <p className="text-sm mb-4 opacity-90">
                15 días gratis del Plan Estándar (7,500 créditos)
              </p>
              <button
                onClick={() => generateCodes('TRIAL15', 5)}
                disabled={generating}
                className="w-full bg-white text-blue-600 font-bold py-3 px-6 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                {generating ? 'Generando...' : 'Generar 5 Códigos Trial'}
              </button>
              <p className="text-xs mt-3 opacity-75">
                Códigos disponibles sin usar: {unusedTrialCodes.length}
              </p>
            </div>
          </div>

          {/* Unused Codes Section */}
          <div className="bg-white dark:bg-[#1a2530] rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              📋 Códigos Disponibles (Sin Usar)
            </h2>
            
            {loading ? (
              <p className="text-gray-600 dark:text-gray-400">Cargando códigos...</p>
            ) : (
              <div className="space-y-6">
                {/* Influencer Codes */}
                {unusedInfluencerCodes.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-purple-600 dark:text-purple-400 mb-3">
                      🌟 Códigos VIP Influencers ({unusedInfluencerCodes.length})
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {unusedInfluencerCodes.map((code) => (
                        <div
                          key={code.id}
                          className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-4"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-mono font-bold text-purple-700 dark:text-purple-300">
                              {code.code}
                            </span>
                            <button
                              onClick={() => copyToClipboard(code.code)}
                              className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-200"
                            >
                              📋
                            </button>
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {code.duration_days} días • {code.credits.toLocaleString()} créditos
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            Creado: {formatDate(code.created_at)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Trial Codes */}
                {unusedTrialCodes.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-blue-600 dark:text-blue-400 mb-3">
                      ⏳ Códigos Prueba Temporal ({unusedTrialCodes.length})
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {unusedTrialCodes.map((code) => (
                        <div
                          key={code.id}
                          className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-mono font-bold text-blue-700 dark:text-blue-300">
                              {code.code}
                            </span>
                            <button
                              onClick={() => copyToClipboard(code.code)}
                              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                            >
                              📋
                            </button>
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {code.duration_days} días • {code.credits.toLocaleString()} créditos
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            Creado: {formatDate(code.created_at)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {unusedInfluencerCodes.length === 0 && unusedTrialCodes.length === 0 && (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    No hay códigos disponibles. Genera algunos códigos arriba.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Used Codes Section */}
          {usedCodes.length > 0 && (
            <div className="bg-white dark:bg-[#1a2530] rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                ✅ Códigos Utilizados ({usedCodes.length})
              </h2>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Código
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Tipo
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Usado en
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Usuario
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {usedCodes.map((code) => (
                      <tr key={code.id} className="border-b border-gray-100 dark:border-gray-800">
                        <td className="py-3 px-4 font-mono text-sm text-gray-900 dark:text-white">
                          {code.code}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            code.code_type === 'INFLUENCER' 
                              ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                          }`}>
                            {code.code_type}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                          {formatDate(code.used_at)}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                          {code.used_by_user_id?.substring(0, 8)}...
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        
        <Footer />
      </div>
    </div>
  );
};

export default AdminInvitations;
